<template lang="pug">
.move-room-schedule-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：種別
    template(#cell(type)="row")
      span {{ convertTypeName(row.item) }}
    // セル：開始
    template(#cell(startDatetime)="row")
      span {{ convertDateText(row.item.startDatetime) }}（
      span(:class="getWeekClass(row.item.startDatetime)") {{ convertWeekText(row.item.startDatetime) }}
      span ）{{ convertTimeText(row.item.startDatetime) }}
    // セル：終了
    template(#cell(endDatetime)="row")
      span {{ convertDateText(row.item.endDatetime) }}（
      span(:class="getWeekClass(row.item.endDatetime)") {{ convertWeekText(row.item.endDatetime) }}
      span ）{{ convertTimeText(row.item.endDatetime) }}
    // セル：登録情報
    template(#cell(operation)="row")
      button.btn.edit-btn.mr-2(type="button" @click="onEdit(row.item)") 修正
      button.btn.delete-btn(type="button" @click="onDelete(row.item)") 削除
</template>

<script lang="ts">
import { MoveRoomSchedulesGetModel, MoveRoomSchedulesType } from "@/commons/services/MoveRoomSchedules";
import { Component, Prop, Vue } from "vue-property-decorator";

/**
 * ### 仮押さえ／転床テーブルコンポーネント
 */
@Component({})
export default class MoveRoomScheduleTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<MoveRoomScheduleTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<MoveRoomScheduleTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  get _value(): Array<MoveRoomScheduleTableItem> {
    return this.value;
  }

  /**
   * ### 種別を表示文字列に変換する
   */
  public convertTypeName(item: MoveRoomScheduleTableItem): string {
    if (item.type == MoveRoomSchedulesType.CHANGE_ROOM) {
      return "転床";
    } else if (item.type == MoveRoomSchedulesType.TEMPORARY) {
      return "仮押さえ";
    } else {
      return "";
    }
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertDateText(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日");
    return date;
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertWeekText(datetime: number): string {
    const week: string = this.getWeekName(this.$dateUtil.convertNumberToDate(datetime));

    return week;
  }

  /**
   * ### 曜日文字列を取得する
   */
  public getWeekName(date: Date): string {
    const weekNameMap: Map<number, string> = new Map([
      [0, "日"],
      [1, "月"],
      [2, "火"],
      [3, "水"],
      [4, "木"],
      [5, "金"],
      [6, "土"]
    ]);
    const week: string | undefined = weekNameMap.get(date.getDay());

    return week ? week : "";
  }

  /**
   * ### 曜日のCSSクラスを返す
   *
   * - 日曜日、土曜日の場合は文字色指定のクラスを返す
   */
  public getWeekClass(datetime: number): string {
    const week: string = this.getWeekName(this.$dateUtil.convertNumberToDate(datetime));

    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertTimeText(datetime: number): string {
    const time: string = this.$dateUtil.convertNumberToString(datetime, "hh：MM");

    return time;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: MoveRoomScheduleTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: MoveRoomScheduleTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface MoveRoomScheduleTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface MoveRoomScheduleTableItem extends MoveRoomSchedulesGetModel {}
</script>

<style lang="scss" scoped>
.move-room-schedule-table-component {
  .edit-btn,
  .delete-btn {
    width: 64px;
  }
}
</style>
